import { Asset } from 'app/shared/collections/asset';
import { KMLFeatureLayer } from 'app/shared/collections/kml_feature_layer';
import { Zone } from 'app/shared/collections/zones/zone';
import { Selection } from 'app/shared/selection/Selection';
import { SelectionType } from 'app/shared/selection/SelectionType';
import { RFTransmitter } from 'app/views/view_types/network/features/rf_planning/collections/rf_transmitter';
import { RFTransmitterCollection } from 'app/views/view_types/network/features/rf_planning/collections/rf_transmitter_collection.service';
import { ConnectionService } from 'app/views/view_types/network/shared/connections/connection.service';
import { ZoneCollection } from '../collections/zones/zone_collection.service';
import { AssetCollection } from '../collections/asset_collection.service';
import { IpDeviceCollection } from '../collections/ip_device_collection.service';
import { IpDevice } from '../collections/ip_device';
import { ConnectionStat } from 'app/views/view_types/network/shared/connections/connectionStat';
import { KMLFeatureLayerCollection } from 'app/shared/collections/kml_feature_layer_collection.service';

function baseSelectReduce<T>(
    selection: Array<Selection>,
    type: string,
    fetch: (id: number | string) => T,
): Array<T> {
    return selection.reduce((objects, selection) => {
        if (selection.type === type) {
            const obj = fetch(selection.id);
            if (obj) {
                return objects.concat([obj]);
            }
        }
        return objects;
    }, []);
}

export function selectionsToSetOfAssetIDs(selections: Selection[]): Set<number | string> {
    if (!selections) {
        return new Set() as Set<number>;
    }

    return new Set(
        selections
            .filter(
                (selection: Selection) =>
                    selection.type === SelectionType.ASSET ||
                    selection.type === SelectionType.ENTITY,
            )
            .map((selection: Selection) => {
                return selection.id;
            }),
    );
}

export function getSelectedAssets(
    selection: Array<Selection>,
    assetCollection: AssetCollection,
): Array<Asset> {
    return baseSelectReduce(selection, SelectionType.ASSET, (id) => assetCollection.get(id));
}

export function getSelectedIPDevices(
    selection: Array<Selection>,
    ipDeviceCollection: IpDeviceCollection,
): Array<IpDevice> {
    return baseSelectReduce(selection, SelectionType.IP_DEVICE, (id) => ipDeviceCollection.get(id));
}

export function getSelectedZones(
    selection: Array<Selection>,
    zoneCollection: ZoneCollection,
): Array<Zone> {
    return baseSelectReduce(selection, SelectionType.ZONE, (id) => zoneCollection.get(id));
}

export function getSelectedKMLFeatureLayers(
    selection: Array<Selection>,
    kmlFeatureLayerCollection: KMLFeatureLayerCollection,
): Array<KMLFeatureLayer> {
    return baseSelectReduce(selection, 'kmlFeatureLayer', (id) =>
        kmlFeatureLayerCollection.get(id),
    );
}

export function getSelectedConnectionEntities(
    selection: Array<Selection>,
    connectionService: ConnectionService,
): Array<ConnectionStat> {
    return baseSelectReduce(selection, SelectionType.CONNECTION, (id) => {
        const ref = connectionService.idToReference(id as string);
        return connectionService.getConnectionStat(ref.fromId, ref.toId);
    });
}

export function getSelectedRfTransmitters(
    selection,
    rfTransmitterCollection: RFTransmitterCollection,
): Array<RFTransmitter> {
    return baseSelectReduce(selection, SelectionType.RF_TRANSMITTER, (id) =>
        rfTransmitterCollection.get(id),
    );
}
